/**
 * La vista «Por turno» de la bandeja de canjes (0375). Módulo PURO: sin red, sin reloj.
 *
 * Una fila por jornada programada, tenga canjes o no, como la bandeja de «Visitas a puestos». Pedido del usuario
 * (2-nov): *«por cada turno programado o planificado, debería haber un espacio para completar»*. Los turnos en cero
 * solo se pueden perseguir si se ven, y en la lista de canjes no aparecen nunca.
 *
 * ⚠️ El estado sale de la FECHA y de quién está asignado, nunca de inferirlo de que algo dio cero (CLAUDE.md, «un
 * contador en cero no es un estado»): un turno de mañana con cero canjes no es deuda.
 */
import type { CanjeRow, JornadaCanje } from '../api/canjes';

export type EstadoTurnoCanje = 'programado' | 'en_curso' | 'sin_persona' | 'sin_canjes' | 'con_canjes';

export const ESTADO_TURNO_LABEL: Record<EstadoTurnoCanje, string> = {
  programado: 'Programado',
  en_curso: 'Hoy',
  sin_persona: 'Sin persona asignada',
  sin_canjes: 'Sin canjes',
  con_canjes: 'Con canjes',
};

/** Tonos con texto de 4,5:1 o más sobre su fondo (CLAUDE.md, «el contraste no tiene dirección»). */
export const ESTADO_TURNO_CLS: Record<EstadoTurnoCanje, string> = {
  programado: 'bg-gray-100 text-gray-700',
  en_curso: 'bg-blue-100 text-blue-800',
  sin_persona: 'bg-red-100 text-red-800',
  sin_canjes: 'bg-amber-100 text-amber-900',
  con_canjes: 'bg-green-100 text-green-800',
};

/**
 * Lo que hay que perseguir. «Sin persona» también: o no se trabajó, o se trabajó sin asignar, y en los dos casos
 * alguien tiene que decirlo. «Programado» y «Hoy» todavía no son deuda.
 */
export function esPendiente(estado: EstadoTurnoCanje): boolean {
  return estado === 'sin_canjes' || estado === 'sin_persona';
}

/** `vigentes` = los canjes registrados del turno (los anulados no cuentan). */
export function estadoDeTurno(j: JornadaCanje, vigentes: number, hoy: string): EstadoTurnoCanje {
  if (vigentes > 0) return 'con_canjes';
  if (j.fecha > hoy) return 'programado';
  if (j.asignacion === null) return 'sin_persona';
  if (j.fecha === hoy) return 'en_curso';
  return 'sin_canjes';
}

export interface CanjesPorTurno {
  /** positionId → canjes de ese turno (incluidos los anulados, si la lectura los trajo). */
  porTurno: Map<string, CanjeRow[]>;
  /**
   * Los que no calzan en ningún turno de la lista: registrados por alguien sin turno ese día en esa tienda. NO se
   * pierden en silencio: la vista los cuenta y manda a la lista por canje.
   */
  sueltos: CanjeRow[];
}

/**
 * Pone cada canje dentro de su turno.
 *
 * Primero por `assignmentId`, que es como lo amarra el servidor. Un canje sin turno (o con un turno que no está en la
 * lista) cae en la jornada de ESA persona en ESA tienda ESE día, si existe: es el caso de quien registró fuera de
 * programación en su propio puesto. Si tiene dos turnos ese día ahí, va al primero de la lista; da igual cuál, el
 * número de la tienda y el día no cambia.
 */
export function agruparCanjesPorTurno(jornadas: JornadaCanje[], canjes: CanjeRow[]): CanjesPorTurno {
  const porAsignacion = new Map<string, string>();
  const porPersonaDia = new Map<string, string>();
  const porTurno = new Map<string, CanjeRow[]>();
  for (const j of jornadas) {
    porTurno.set(j.positionId, []);
    if (j.asignacion === null) continue;
    porAsignacion.set(j.asignacion.id, j.positionId);
    const clave = `${j.asignacion.workerId}|${j.pdv.id}|${j.fecha}`;
    if (!porPersonaDia.has(clave)) porPersonaDia.set(clave, j.positionId);
  }
  const sueltos: CanjeRow[] = [];
  for (const c of canjes) {
    const directo = c.assignmentId === null ? undefined : porAsignacion.get(c.assignmentId);
    const destino = directo
      ?? (c.worker && c.pdv ? porPersonaDia.get(`${c.worker.id}|${c.pdv.id}|${c.fecha}`) : undefined);
    if (destino === undefined) sueltos.push(c);
    else porTurno.get(destino)!.push(c);
  }
  return { porTurno, sueltos };
}

/** Canjes que cuentan (suma de líneas), solo de los registros vigentes. */
export function canjesVigentes(filas: CanjeRow[]): number {
  return filas.reduce((s, f) => s + (f.estado === 'registrado' ? f.totalCanjes : 0), 0);
}
